import { Injectable, inject } from '@angular/core';
import { Map, Marker } from 'maplibre-gl';
import { BehaviorSubject, first, fromEvent } from 'rxjs';
import { Subscription } from 'rxjs/internal/Subscription';
import { WaypointService } from '../core/waypoint.service';
import { MapSkin, SettingsService } from '../account/settings.service';
import { LanguageService } from '../account/language.service';
import { MapDrawerHelperService } from './map-drawer-helper.service';

@Injectable({
  providedIn: 'root',
})
export class MapPoiDrawerService {
  private mapInstance: Map | null = null;
  private poiMarker: Marker | null = null;
  private popupSubscriptions: Subscription[] = [];
  private mapClickDismissHandler: (() => void) | null = null;
  private removeNewGestureListener: (() => void) | null = null;
  private canDismissPoi = false;
  public observableDrawPoi: BehaviorSubject<{ latitude: number, longitude: number }> = new BehaviorSubject<{ latitude: number, longitude: number }>({ latitude: 0, longitude: 0 });
  public observablePlanToPoi: BehaviorSubject<{ latitude: number, longitude: number }> =
    new BehaviorSubject<{ latitude: number, longitude: number }>({ latitude: 0, longitude: 0 });

  private readonly waypointService = inject(WaypointService);
  private readonly settingsService = inject(SettingsService);
  private readonly languageService = inject(LanguageService);
  private readonly mapDrawerHelperService = inject(MapDrawerHelperService);

  public setMapInstance(map: Map): void {
    this.mapInstance = map;
  }

  /**
   * Draws the POI card at the given coordinate.
   *
   * `options.showGoButton` (default true) toggles the "Go" action button — the
   * address-panel shows a read-only card without it. `options.dismissOnMapClick`
   * (default true) controls whether clicking elsewhere on the map closes the
   * card; callers that own the card's lifecycle (e.g. the address panel) pass
   * `false` and remove it explicitly.
   */
  public drawPoi(latitude: number, longitude: number, poiData: any,
    options?: { showGoButton?: boolean; dismissOnMapClick?: boolean }): void {
    const showGoButton = options?.showGoButton ?? true;
    const dismissOnMapClick = options?.dismissOnMapClick ?? true;

    this.observableDrawPoi.next({ latitude: latitude, longitude: longitude });

    this.removePoi();

    const isDark = this.settingsService.getMap().Skin === MapSkin.Dark;
    const markerImg = isDark ? 'assets/icons/default/map/poi_marker_dark.svg' : 'assets/icons/default/map/poi_marker.svg';

    const address = poiData.address ?? '';
    const title = this.deriveTitle(poiData);
    const waypointLimitReached = this.waypointService.getWaypointsParams().length >= 5;
    const goButtonInactiveClass = waypointLimitReached ? ' inactive' : '';

    const popupEl = document.createElement("div");
    popupEl.className = "poi-outer";
    popupEl.innerHTML = `
      <div class="poi-card">
        <div class="poi-title">${this.escapeHtml(title)}</div>
        <div class="poi-address-pill">
          <img class="poi-info-icon" src="assets/icons/default/poi_address_home.svg" alt="" />
          <span class="poi-info-text">${this.escapeHtml(address)}</span>
        </div>
        ${showGoButton && waypointLimitReached ? `<div class="poi-limit-warning">${this.escapeHtml(this.languageService.languageJSON.InfoSidebar_WaypointLimitReached)}</div>` : ''}
        ${showGoButton ? `<button class="poi-go-btn ev-button primary${goButtonInactiveClass}" data-tutorial-target="poi-go">${this.escapeHtml(this.languageService.languageJSON.NavigationMenu_Go)}</button>` : ''}
      </div>
      <div class="poi-pin">
        <img src="${markerImg}" alt="" />
      </div>
    `;

    // Attach to a fixed coordinate
    this.poiMarker = new Marker({
      element: popupEl,
      anchor: "bottom"
    })
      .setLngLat([longitude, latitude])
      .addTo(this.mapInstance!);

    // Bring the whole card (it grows upward from the bottom-anchored pin) into
    // the part of the map the floating panels leave free.
    this.mapDrawerHelperService.cardOpened(popupEl);
    this.mapDrawerHelperService.revealCard([longitude, latitude], popupEl);

    if (showGoButton && !waypointLimitReached) {
      const goBtn = popupEl.querySelector('.poi-go-btn')!;

      this.popupSubscriptions.push(
        fromEvent(goBtn, 'click').pipe(first()).subscribe((event) => {
          if ((event as Event).isTrusted) {
            this.observablePlanToPoi.next({ latitude, longitude });
            this.waypointService.addEndpoint({
              display_name: poiData.address,
              lat: latitude,
              lng: longitude
            });
            this.removePoi();
          }
        })
      );
    }

    if (dismissOnMapClick) {
      this.registerDismissOnMapClick();
    }
  }

  public removePoi(): void {
    if (this.poiMarker) {
      this.mapDrawerHelperService.cardClosed(this.poiMarker.getElement());
      this.poiMarker.remove();
      this.poiMarker = null;
    }
    this.popupSubscriptions.forEach(s => s.unsubscribe());
    this.popupSubscriptions = [];

    if (this.mapClickDismissHandler && this.mapInstance) {
      this.mapInstance.off('click', this.mapClickDismissHandler);
    }
    this.mapClickDismissHandler = null;

    if (this.removeNewGestureListener) {
      this.removeNewGestureListener();
      this.removeNewGestureListener = null;
    }
    this.canDismissPoi = false;
  }

  // Title shown in bold above the address. Prefer an explicit title (e.g. the
  // street + house number from reverse geocoding); otherwise fall back to the
  // first segment of the address, and to the full address when there is none.
  private deriveTitle(poiData: any): string {
    if (poiData.title) {
      return poiData.title;
    }
    const address: string = poiData.address ?? '';
    const firstComma = address.indexOf(',');
    return firstComma > 0 ? address.slice(0, firstComma).trim() : address;
  }

  // Dismiss the POI when the user clicks elsewhere on the map. The click that
  // ends the gesture which drew the POI (e.g. the release of a long-press)
  // must be ignored, so dismissal is only armed once a *new* pointer gesture
  // begins after the POI was drawn.
  private registerDismissOnMapClick(): void {
    const map = this.mapInstance;
    if (!map) {
      return;
    }

    this.canDismissPoi = false;
    const canvas = map.getCanvas();
    const onPointerDown = () => { this.canDismissPoi = true; };
    canvas.addEventListener('pointerdown', onPointerDown, { once: true });
    this.removeNewGestureListener = () => canvas.removeEventListener('pointerdown', onPointerDown);

    this.mapClickDismissHandler = () => {
      if (this.canDismissPoi) {
        this.removePoi();
      }
    };
    map.on('click', this.mapClickDismissHandler);
  }

  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}